import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Tab, Tabs, Typography } from '@mui/material';
import { formatSiteDateTime } from '../../domain/siteTime/siteTime';
import {
  prefersReducedMotion,
  visualMarkerLegend,
  type SpatialZoneView,
  type VisualLifecycleViewModel,
  type VisualMarkerKind,
} from '../../domain/continuousSafety/visualLifecycleModel';
import {
  presentCustomerPatternReasonSummary,
  presentCustomerPatternWindowCopy,
  presentCustomerPostControlPatternStatus,
  presentCustomerResultDirection,
} from '../../domain/continuousSafety/conditionLifecyclePresentation';

export type VisualLifecycleDensity = 'page' | 'scan';
export type VisualLifecycleSurface = 'full' | 'spatial' | 'history';

const KIND_LABEL: Record<VisualMarkerKind, string> = {
  occurrence: 'Occurrence',
  immediate: 'Immediate alert',
  pattern: 'Pattern Alert',
  control: 'Control',
  scan: 'Scan',
};

function presentSpatialVerificationState(raw: string): string {
  const match = raw.match(/^Scan (\S+) · coverage (.+)$/);
  if (!match) return raw;
  const direction = presentCustomerResultDirection(match[1]);
  return direction ? `Scan · ${direction} · coverage ${match[2]}` : `Scan · coverage ${match[2]}`;
}

function customerZoneLabel(zoneId: string | null | undefined, zones: SpatialZoneView[]): string | null {
  if (!zoneId) return null;
  const named = zones.find((zone) => zone.zoneId === zoneId)?.name;
  if (named && named !== zoneId) return named;
  const compact = zoneId.toLowerCase().replace(/-/g, '_');
  if (/^[a-z0-9]+(?:_[a-z0-9]+)+$/.test(compact)) return null;
  return named || zoneId;
}

function MarkerGlyph({
  kind,
  selected,
}: {
  kind: VisualMarkerKind;
  selected?: boolean;
}) {
  const size = selected ? 14 : 11;
  const color = {
    occurrence: '#2563eb',
    immediate: '#b45309',
    pattern: '#7c3aed',
    control: '#c2410c',
    scan: '#0f766e',
  }[kind];
  if (kind === 'occurrence') {
    return <Box component="span" aria-hidden sx={{ width: size, height: size, borderRadius: '50%', bgcolor: color, display: 'inline-block' }} />;
  }
  if (kind === 'immediate') {
    return <Box component="span" aria-hidden sx={{ width: size, height: size, bgcolor: color, display: 'inline-block' }} />;
  }
  if (kind === 'pattern') {
    return (
      <Box
        component="span"
        aria-hidden
        sx={{
          width: 0,
          height: 0,
          borderLeft: `${size / 2}px solid transparent`,
          borderRight: `${size / 2}px solid transparent`,
          borderBottom: `${size}px solid ${color}`,
          display: 'inline-block',
        }}
      />
    );
  }
  if (kind === 'control') {
    return <Box component="span" aria-hidden sx={{ width: 3, height: size + 4, bgcolor: color, display: 'inline-block' }} />;
  }
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        width: size,
        height: size,
        bgcolor: color,
        display: 'inline-block',
        transform: 'rotate(45deg)',
      }}
    />
  );
}

function LifecycleAnchor({
  href,
  children,
  onNavigate,
  testId,
}: {
  href: string | null;
  children: React.ReactNode;
  onNavigate?: (path: string) => void;
  testId?: string;
}) {
  if (!href) return <Box component="span" data-testid={testId}>{children}</Box>;
  return (
    <Box
      component="a"
      href={href}
      data-testid={testId}
      onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        onNavigate?.(href);
      }}
      sx={{ color: '#1d4ed8', textDecoration: 'none', fontWeight: 650 }}
    >
      {children}
    </Box>
  );
}

function ZoneMap({ zones, fill }: { zones: SpatialZoneView[]; fill?: boolean }) {
  const geometryZones = zones.filter((zone) => zone.polygonNorm || zone.bboxNorm);
  if (geometryZones.length === 0) return null;
  const points = geometryZones.flatMap((zone) => zone.polygonNorm || []);
  const xs = points.map((point) => point[0]);
  const ys = points.map((point) => point[1]);
  const minX = Math.min(0, ...xs);
  const minY = Math.min(0, ...ys);
  const maxX = Math.max(1, ...xs);
  const maxY = Math.max(1, ...ys);
  const toSvg = (x: number, y: number) => [
    ((x - minX) / (maxX - minX || 1)) * 100,
    ((y - minY) / (maxY - minY || 1)) * 100,
  ];
  return (
    <Box
      component="svg"
      viewBox="0 0 100 56"
      preserveAspectRatio="xMidYMid meet"
      sx={{
        width: '100%',
        maxWidth: fill ? 'none' : 520,
        height: fill ? { xs: 220, md: 360 } : 'auto',
        minHeight: fill ? { xs: 220, md: 360 } : undefined,
        border: '1px solid #d5deea',
        bgcolor: '#f8fafc',
        display: 'block',
      }}
      role="img"
      aria-label="Authoritative zone outlines"
      data-testid="visual-lifecycle-zone-geometry"
    >
      {geometryZones.map((zone) => {
        const poly = (zone.polygonNorm || []).map((point) => toSvg(point[0], point[1]).join(',')).join(' ');
        return (
          <polygon
            key={zone.zoneId}
            points={poly}
            fill={zone.recurrenceVisible ? 'rgba(37,99,235,0.18)' : 'rgba(148,163,184,0.16)'}
            stroke="#1d4ed8"
            strokeWidth="1.2"
          />
        );
      })}
    </Box>
  );
}

export function VisualLifecycleExperience({
  model,
  timeZone,
  onNavigate,
  density = 'page',
  focus,
  surface = 'full',
}: {
  model: VisualLifecycleViewModel;
  timeZone?: string | null;
  onNavigate?: (path: string) => void;
  density?: VisualLifecycleDensity;
  focus?: string | null;
  surface?: VisualLifecycleSurface;
}) {
  const [view, setView] = useState<'evidence' | 'spatial'>(focus === 'evidence' ? 'evidence' : 'spatial');
  const [selectedId, setSelectedId] = useState<string | null>(
    model.markers.find((item) => item.kind === 'pattern')?.id
    || model.markers.find((item) => item.kind === 'control')?.id
    || null,
  );
  const [playing, setPlaying] = useState(false);
  const compact = density === 'scan';
  const selected = model.markers.find((item) => item.id === selectedId) || null;
  const selectedPattern = selected?.kind === 'pattern' ? selected : model.markers.find((item) => item.kind === 'pattern') || null;

  useEffect(() => {
    if (!playing) return;
    if (prefersReducedMotion() || model.markers.length === 0) {
      setPlaying(false);
      return;
    }
    const timer = window.setInterval(() => {
      setSelectedId((current) => {
        const index = model.markers.findIndex((item) => item.id === current);
        const next = model.markers[index + 1];
        if (!next) {
          setPlaying(false);
          return current;
        }
        return next.id;
      });
    }, 900);
    return () => window.clearInterval(timer);
  }, [playing, model.markers]);

  const playDisabled = prefersReducedMotion() || model.markers.length === 0;
  const pivotIndex = model.markers.findIndex((item) => item.kind === 'control');
  const showFullChrome = surface === 'full';
  const showSpatial = surface === 'spatial' || (showFullChrome && !compact && view === 'spatial');
  const showEvidence = showFullChrome && !compact && view === 'evidence';
  const showHistory = surface === 'history' || surface === 'full';

  const timeLabel = (at: string | null) => {
    if (!at) return 'time unavailable';
    return formatSiteDateTime(at, timeZone) || at;
  };

  const comparisonCopy = useMemo(() => {
    if (!model.comparison.hasControlPivot) {
      return 'No Control is linked, so Before and Since are not compared.';
    }
    return 'Before vs Since uses Overview recurrence, the linked Control time, Pattern Alert status after Control, and Scan verification. Counts are not recalculated here.';
  }, [model.comparison.hasControlPivot]);

  return (
    <Box
      data-testid={compact ? 'scan-visual-lifecycle' : 'visual-lifecycle'}
      data-visual-density={density}
      sx={{ mt: compact ? 1.5 : 3, overflowX: 'hidden' }}
    >
      {compact ? (
        <Typography variant="overline" color="text.secondary">Visual verification context</Typography>
      ) : showFullChrome ? (
        <>
          <Typography variant="overline" color="text.secondary">Visual lifecycle</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Camera/Evidence shows recorded stills. Spatial shows the linked area or zone.
            The timeline is Recurrence → Alarm → Control → Since → Verification.
            Control is the pivot only when a Control is recorded.
          </Typography>
        </>
      ) : null}

      {showHistory && surface !== 'history' ? (
      <Box
        data-testid="visual-time-semantics"
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mb: 1.5 }}
      >
        <Typography variant="caption" data-testid="visual-recurrence-range">
          Recurrence · {model.timeSemantics.recurrenceRange.summary}
        </Typography>
        <Typography variant="caption" data-testid="visual-pattern-window">
          {model.timeSemantics.patternWindow.summary}
        </Typography>
        <Typography variant="caption" data-testid="visual-since-anchor">
          {model.timeSemantics.sinceAnchor.summary}
        </Typography>
        <Typography variant="caption" data-testid="visual-scan-interval">
          {model.timeSemantics.scanInterval.summary}
        </Typography>
      </Box>
      ) : null}

      {showFullChrome && !compact ? (
        <Tabs
          value={view}
          onChange={(_event, next) => setView(next)}
          sx={{ minHeight: 36, mb: 1 }}
          aria-label="Camera evidence or spatial view"
        >
          <Tab value="evidence" label="Camera / Evidence" data-testid="visual-tab-evidence" sx={{ minHeight: 36 }} />
          <Tab value="spatial" label="Spatial" data-testid="visual-tab-spatial" sx={{ minHeight: 36 }} />
        </Tabs>
      ) : null}
      {showEvidence ? (
            <Box data-testid="visual-evidence-pane" sx={{ border: '1px solid #d5deea', borderRadius: 1, p: 1.5, mb: 2 }}>
              {model.partial.offlineCamera ? (
                <Typography data-testid="visual-offline-camera" color="text.secondary">
                  Camera coverage is offline or telemetry is missing. Spatial and timeline remain available.
                </Typography>
              ) : null}
              {model.evidence.status === 'ready' && model.evidence.imageUrl ? (
                <Box
                  component="img"
                  src={model.evidence.imageUrl}
                  alt="Recorded condition evidence"
                  data-testid="visual-evidence-image"
                  sx={{ width: '100%', maxHeight: 280, objectFit: 'contain' }}
                />
              ) : (
                <Typography data-testid="visual-missing-evidence" color="text.secondary">
                  {model.partial.offlineCamera
                    ? 'No evidence still is available while the camera is offline.'
                    : 'No recorded evidence still is linked for this condition yet.'}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Cameras {model.cameraIds.join(', ') || 'not named'} · {model.areaLabel}
              </Typography>
              <LifecycleAnchor href={model.evidenceHref} onNavigate={onNavigate} testId="visual-evidence-link">
                Open evidence
              </LifecycleAnchor>
            </Box>
      ) : null}
      {showSpatial ? (
            <Box data-testid="visual-spatial-pane" sx={{ border: '1px solid #d5deea', borderRadius: 1, p: 1.5, mb: 2 }}>
              {model.spatialMode === 'unavailable' ? (
                <Typography data-testid="visual-spatial-honesty">{model.spatialHonesty}</Typography>
              ) : (
                <>
                  {model.spatialMode === 'zone_named' ? (
                    <Typography variant="h6" sx={{ fontWeight: 750, mb: 1 }} data-testid="visual-spatial-named-zone">
                      {model.spatialZones.map((zone) => zone.name).filter(Boolean).join(' · ') || 'Named zone'}
                    </Typography>
                  ) : null}
                  <ZoneMap zones={model.spatialZones} fill={surface === 'spatial'} />
                  {model.spatialZones.map((zone) => (
                    <Box key={zone.zoneId} data-testid={`visual-spatial-zone-${zone.zoneId}`} sx={{ mt: 1 }}>
                      <Typography variant="subtitle2">{zone.name}</Typography>
                      <Typography variant="caption" display="block" data-testid="visual-spatial-recurrence">
                        {zone.recurrenceValue == null
                          ? 'Recurrence history unavailable'
                          : (zone.recurrenceVisible ? 'Recurring in this zone' : 'No recorded recurrence in this zone')}
                      </Typography>
                      {zone.recurrenceValue != null ? (
                        <Typography variant="caption" display="block" data-testid="visual-spatial-recorded-recurrence">
                          {zone.recurrenceValue} recorded occurrences in recurrence history
                        </Typography>
                      ) : null}
                      <Typography variant="caption" display="block" data-testid="visual-spatial-linked-occurrences">
                        Lifecycle-linked occurrences: {zone.linkedOccurrenceCount == null ? 'unavailable' : zone.linkedOccurrenceCount}
                      </Typography>
                      <Typography variant="caption" display="block" data-testid="visual-spatial-alert">{zone.alertState}</Typography>
                      <Typography variant="caption" display="block" data-testid="visual-spatial-control">{zone.controlState}</Typography>
                      <Typography variant="caption" display="block" data-testid="visual-spatial-verification">
                        {presentSpatialVerificationState(zone.verificationState)}
                      </Typography>
                    </Box>
                  ))}
                  <Typography variant="caption" color="text.secondary" data-testid="visual-spatial-honesty" sx={{ display: 'block', mt: 1.25 }}>
                    {model.spatialHonesty}
                  </Typography>
                </>
              )}
            </Box>
      ) : null}

      {showHistory ? (
      <>
      <Box data-testid="visual-lifecycle-timeline" sx={{ border: '1px solid #d5deea', borderRadius: 1, p: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Typography variant="subtitle2">
            {model.comparison.hasControlPivot ? 'Before Control · Control · Since Control' : 'Lifecycle timeline'}
          </Typography>
          {!compact ? (
            <Button
              size="small"
              variant="outlined"
              disabled={playDisabled}
              onClick={() => setPlaying((value) => !value)}
              data-testid="visual-play"
              aria-label={playing ? 'Pause timeline' : 'Play timeline'}
            >
              {playing ? 'Pause' : 'Play'}
            </Button>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1 }} aria-label="Timeline legend">
          {visualMarkerLegend().map((item) => (
            <Typography key={item.kind} variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
              <MarkerGlyph kind={item.kind} /> {item.label}
            </Typography>
          ))}
        </Box>
        <Box
          role="list"
          aria-label="Condition lifecycle timeline"
          sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, overflowX: 'hidden' }}
        >
          {model.markers.length === 0 ? (
            <Typography color="text.secondary">No linked lifecycle events in this period.</Typography>
          ) : model.markers.map((marker, index) => (
            <React.Fragment key={`${marker.kind}-${marker.id}`}>
              {model.comparison.hasControlPivot && index === pivotIndex ? (
                <Box data-testid="visual-control-pivot" sx={{ borderLeft: '2px dashed #c2410c', px: 1, py: 0.5 }}>
                  <Typography variant="caption" fontWeight={700}>Control</Typography>
                </Box>
              ) : null}
              <Box role="listitem" sx={{ display: 'inline-flex' }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setSelectedId(marker.id)}
                  data-testid={`visual-marker-${marker.kind}-${marker.id}`}
                  aria-label={`${KIND_LABEL[marker.kind]} at ${timeLabel(marker.at)}`}
                  aria-pressed={selectedId === marker.id}
                  sx={{
                    border: selectedId === marker.id ? '2px solid #1d4ed8' : '1px solid #d5deea',
                    bgcolor: marker.side === 'since' ? '#ecfdf3' : (marker.side === 'before' ? '#eff6ff' : '#fff'),
                    borderRadius: 1,
                    px: 0.8,
                    py: 0.5,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.6,
                  }}
                >
                  <MarkerGlyph kind={marker.kind} selected={selectedId === marker.id} />
                  <Typography variant="caption">{KIND_LABEL[marker.kind]}</Typography>
                </Box>
              </Box>
            </React.Fragment>
          ))}
        </Box>
        {(model.comparison.recurrenceValue || 0) > 0 && model.lifecycleOccurrenceCount === 0 ? (
          <Typography variant="caption" color="text.secondary" data-testid="visual-recurrence-linkage-note" sx={{ display: 'block', mt: 1 }}>
            Recorded recurrence history is available. Occurrence-level lifecycle links are not available for this history.
          </Typography>
        ) : null}
        {!model.comparison.hasControlPivot ? (
          <Typography variant="caption" color="text.secondary" data-testid="visual-no-control-pivot" sx={{ display: 'block', mt: 1 }}>
            No Control is recorded, so the timeline is not split into Before and Since.
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary" data-testid="visual-before-since-distinct" sx={{ display: 'block', mt: 1 }}>
            Before Control is on the left of the Control marker. Since Control is on the right.
          </Typography>
        )}
        {selected ? (
          <Box sx={{ mt: 1 }} data-testid="visual-selected-marker">
            <Typography variant="body2">{selected.label} · {timeLabel(selected.at)}</Typography>
            {selected.href ? (
              <LifecycleAnchor
                href={selected.href}
                onNavigate={onNavigate}
                testId={`visual-selected-${selected.kind}-link`}
              >
                Open {KIND_LABEL[selected.kind]}
              </LifecycleAnchor>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Qualified occurrences are shown on the timeline; they are not a separate alarm.
              </Typography>
            )}
          </Box>
        ) : null}
      </Box>

      <Box data-testid="visual-before-since" sx={{ mt: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1 }}>
        <Box sx={{ border: '1px solid #d5deea', borderRadius: 1, p: 1 }}>
          <Typography variant="caption" color="text.secondary">Before Control</Typography>
          <Typography variant="body2" data-testid="visual-before-recurrence">
            Recurrence: {model.comparison.recurrenceValue ?? 'unavailable'} {model.comparison.recurrenceUnits || ''}
          </Typography>
          <Typography variant="caption">
            Pattern Alerts {model.comparison.hasControlPivot ? (model.comparison.patternAlertsBefore ?? 0) : 'n/a'}
          </Typography>
        </Box>
        <Box sx={{ border: '1px solid #d5deea', borderRadius: 1, p: 1 }}>
          <Typography variant="caption" color="text.secondary">Since Control</Typography>
          <Typography variant="body2" data-testid="visual-since-recurrence">
            Since {model.comparison.hasControlPivot ? (model.comparison.sinceValue ?? 'unavailable') : 'no Control'} {model.comparison.sinceUnits || ''}
          </Typography>
          <Typography variant="caption" data-testid="visual-since-pattern">
            Pattern Alerts {model.comparison.hasControlPivot ? model.comparison.patternAlertsSince : 'n/a'}
            {surface === 'history'
              ? (presentCustomerPostControlPatternStatus(model.comparison.patternStatus)
                ? ` · ${presentCustomerPostControlPatternStatus(model.comparison.patternStatus)}`
                : '')
              : ` · ${model.comparison.patternStatus}`}
          </Typography>
        </Box>
        <Box sx={{ border: '1px solid #d5deea', borderRadius: 1, p: 1 }}>
          <Typography variant="caption" color="text.secondary">Verification</Typography>
          <Typography variant="body2" data-testid="visual-scan-result">
            {surface === 'history'
              ? (presentCustomerResultDirection(model.comparison.scanResult)
                || (model.comparison.scanResult ? 'Comparison linked' : 'No Scan linked'))
              : (model.comparison.scanResult || 'No Scan linked')}
          </Typography>
          {model.comparison.coverageFinalityVisible ? (
            <Typography variant="caption" data-testid="visual-coverage-finality">
              Coverage {model.comparison.coverageState || 'unavailable'} · {model.comparison.finality || 'finality unavailable'}
            </Typography>
          ) : (
            <Typography variant="caption">Coverage and finality are not linked.</Typography>
          )}
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" data-testid="visual-comparison-authority">{comparisonCopy}</Typography>
      {model.partial.insufficientCoverage ? (
        <Typography variant="caption" data-testid="visual-insufficient-coverage" color="text.secondary" sx={{ display: 'block' }}>
          Coverage is insufficient, so verification remains qualified by Scan coverage state.
        </Typography>
      ) : null}

      {selectedPattern ? (
        <Box data-testid="visual-pattern-reason" sx={{ mt: 1.5, border: '1px solid #d5deea', borderRadius: 1, p: 1.5 }}>
          <Typography variant="subtitle2">Why this Pattern Alert fired</Typography>
          <Typography variant="body2">
            {surface === 'history'
              ? (presentCustomerPatternReasonSummary(model.patternReason) || selectedPattern.label)
              : (model.patternReason?.summary || selectedPattern.label)}
          </Typography>
          {model.patternMembers.length ? (
            <Box component="ol" sx={{ pl: 2, mb: 0 }} data-testid="visual-pattern-members">
              {model.patternMembers.map((member, index) => (
                <Typography component="li" variant="body2" key={member.occurrenceId}>
                  Contributing occurrence {index + 1}
                  {member.isThreshold ? ' · crossed the threshold' : ''}
                  {member.occurredAt ? ` · ${timeLabel(member.occurredAt)}` : ''}
                  {customerZoneLabel(member.zoneId, model.spatialZones) ? ` · ${customerZoneLabel(member.zoneId, model.spatialZones)}` : ''}
                </Typography>
              ))}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">Source occurrences are not linked.</Typography>
          )}
          <LifecycleAnchor href={selectedPattern.href} onNavigate={onNavigate} testId="visual-pattern-incident-link">
            Open Pattern Alert
          </LifecycleAnchor>
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary" data-testid="visual-no-pattern" sx={{ display: 'block', mt: 1 }}>
          No Pattern Alert is linked.
        </Typography>
      )}

      {surface === 'history' ? (
        <Box
          data-testid="visual-time-semantics"
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mt: 1.5 }}
        >
          <Typography variant="caption" color="text.secondary" data-testid="visual-recurrence-range">
            {model.timeSemantics.recurrenceRange.summary}
          </Typography>
          <Typography variant="caption" color="text.secondary" data-testid="visual-pattern-window">
            {presentCustomerPatternWindowCopy(
              model.timeSemantics.patternWindow.minEvents,
              model.timeSemantics.patternWindow.windowSeconds,
            ) || 'No Pattern Alert window is linked.'}
          </Typography>
          <Typography variant="caption" color="text.secondary" data-testid="visual-since-anchor">
            {model.timeSemantics.sinceAnchor.summary}
          </Typography>
          <Typography variant="caption" color="text.secondary" data-testid="visual-scan-interval">
            {model.timeSemantics.scanInterval.summary}
          </Typography>
        </Box>
      ) : null}

      {model.partial.noRecurrence ? (
        <Typography variant="caption" data-testid="visual-no-recurrence" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          No recurrence is linked for this condition yet.
        </Typography>
      ) : null}
      {model.partial.noImmediate ? (
        <Typography variant="caption" data-testid="visual-no-immediate" color="text.secondary" sx={{ display: 'block' }}>
          No immediate alert is linked.
        </Typography>
      ) : null}
      {model.partial.noScan ? (
        <Typography variant="caption" data-testid="visual-no-scan" color="text.secondary" sx={{ display: 'block' }}>
          No Scan verification is linked.
        </Typography>
      ) : null}
      {model.partial.legacyPartial ? (
        <Typography variant="caption" data-testid="visual-legacy-partial" color="text.secondary" sx={{ display: 'block' }}>
          This lifecycle is partial or legacy. Missing Control or Scan links are not inferred from timestamps.
        </Typography>
      ) : null}
      </>
      ) : null}
    </Box>
  );
}
